/**
 * 移動平均ベースの強度（intensity）推定
 *
 * 利点:
 * - 端点バイアスなし（過去のデータのみ使用）
 * - 解釈が直感的（「この期間の平均発生率」）
 * - 計算が速い
 * - 帯域幅の自動決定が不要
 */

/**
 * イベント時点データから移動平均ベースの強度を計算
 *
 * @param eventTimes - イベント発生時刻の配列（ミリ秒単位）
 * @param windowSize - ウィンドウサイズ（ミリ秒単位）
 * @param evaluationPoints - 強度を評価する時点の配列（ミリ秒単位）
 * @returns [時点, 強度]の配列
 */
export function movingAverageIntensity(
  eventTimes: number[],
  windowSize: number,
  evaluationPoints: number[]
): [number, number][] {
  if (eventTimes.length === 0) {
    return evaluationPoints.map(t => [t, 0]);
  }

  // イベント時刻をソート（効率的な検索のため）
  const sortedEvents = [...eventTimes].sort((a, b) => a - b);

  return evaluationPoints.map(t => {
    // 後ろ向きウィンドウ: [t - windowSize, t]
    // 端点でも安定（未来のデータを使わない）
    const windowStart = t - windowSize;
    const windowEnd = t;

    // ウィンドウ内のイベント数を数える
    let count = 0;
    for (const eventTime of sortedEvents) {
      if (eventTime > windowEnd) break; // ソート済みなので打ち切り
      if (eventTime >= windowStart && eventTime <= windowEnd) {
        count++;
      }
    }

    // 強度 = イベント数 / ウィンドウサイズ（単位時間あたり）
    const intensity = count / windowSize;

    return [t, intensity];
  });
}

/**
 * 時間範囲に応じて適切なウィンドウサイズを決定
 *
 * @param timeRange - 全体の時間範囲（ミリ秒）
 * @returns ウィンドウサイズ（ミリ秒）
 */
export function determineWindowSize(timeRange: number): number {
  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;

  if (timeRange < 2 * oneDay) {
    // 2日未満: 3時間ウィンドウ
    return 3 * oneHour;
  } else if (timeRange < 7 * oneDay) {
    // 1週間未満: 6時間ウィンドウ
    return 6 * oneHour;
  } else if (timeRange < 30 * oneDay) {
    // 1ヶ月未満: 1日ウィンドウ
    return oneDay;
  } else if (timeRange < 365 * oneDay) {
    // 1年未満: 3日ウィンドウ
    return 3 * oneDay;
  } else {
    // 1年以上: 7日ウィンドウ
    return 7 * oneDay;
  }
}
